import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { FIRST_FILL, SUGGESTIONS_STATUS } from "../constants/suggestions"
import { normalizeTag } from "../utils/tags"
import { RECEIPT_CONFIGURE_SLICE_NAME as SLICE_NAME } from "./configure.constants"

/* ------------- Helpers ------------- */

// Hydration collapses every blank answer to `undefined` (utils/hydrate contentSection),
// while the controls write back "" for a cleared input, `false` for an unticked
// checkbox and [] for an emptied chip list. Dirty-checking looks through the same
// "nothing here" lens, so answering a field the server never filled and then undoing
// it is not a change. Dropping a blank from both sides keeps real edits visible: a
// value the server did fill stays on the baseline side and the comparison still fails.
const isBlank = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  value === false ||
  (Array.isArray(value) && value.length === 0)

const withoutBlanks = (value) => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return value
  const out = {}
  for (const [key, entry] of Object.entries(value)) {
    if (isBlank(entry)) continue
    out[key] = withoutBlanks(entry)
  }
  return out
}

const withAdded = (current, additions) => {
  const merged = [...(current || [])]
  const seen = new Set(merged.map(normalizeTag))

  for (const value of additions) {
    const key = normalizeTag(value)
    if (seen.has(key)) continue
    seen.add(key)
    merged.push(value)
  }

  return merged
}

// The first fill (CHA1-4909) for one copy of Step 3. ARNs wait while "use BIN and CAID"
// is ticked and are filled in once it is cleared (§11.9).
// `replaceArn` refills an ARN field left empty or invalid, as a reload would show it.
const withSuggestions = (identifiers, { firstFill, descriptors, arnCodes, replaceArn }) => {
  const next = { ...identifiers }

  if (firstFill === FIRST_FILL.ALL)
    next.billing_descriptors = withAdded(next.billing_descriptors, descriptors)

  if (!next.use_bin_caid) next.arn_codes = withAdded(replaceArn ? [] : next.arn_codes, arnCodes)

  return next
}

/* ------------- Initial State ------------- */

// Configure wizard state (§11 in CHA1-1769-related docs). The whole thing is
// server-owned: on every mount the page calls externalOps.loadProfile() and then
// hydrateConfigure() replaces this slice wholesale — nothing here is persisted
// across sessions. saveSection is local-only (marks a section dirty); the actual
// PATCH/POST goes through externalOps.saveDraft/submitProfile from the components.

const initialState = {
  profile: {
    // Wizard answers, keyed by step (business_info / business_details / ...).
    buildingBlocks: {},
    // The answers exactly as the last Configure GET delivered them. A section is
    // dirty only while it differs from this snapshot, so editing a field and
    // typing the original value back leaves nothing dirty. Deliberately NOT
    // refreshed by a draft save: for an already-submitted profile the baseline
    // is the live version, and "Update details" must stay available until the
    // change is actually submitted for review.
    baselineBlocks: {},
    // Server's own wizard block (completed_steps + mode flags). Single source of
    // truth for "is this step Done" and "is setup finished".
    wizard: null,
    // Business account this state was hydrated for. Null = "not hydrated yet":
    // consumers must not save, and must not trust what they are rendering.
    hydratedBaId: null,
    // Live version a Profile update is written against (§11.13). Server-owned.
    baseVersionId: null,
    // Serializer's `updated` field, shown as "Updated <date>" on the profile card.
    lastUpdated: null,
    // Set true after a Profile update is submitted, until the mock backend applies.
    hasPendingChanges: false
  },
  ui: {
    // idle | saving | saved | error — drives the footer save-status pill.
    autoSaveStatus: "idle",
    // { [sectionKey]: true } for every section with unsaved local edits.
    dirtySections: {}
  },
  // Step 3 first fill (CHA1-4909): the request's progress and what the integrations said.
  suggestions: {
    status: SUGGESTIONS_STATUS.IDLE,
    // Which fields the running fill is for (FIRST_FILL).
    firstFill: null,
    integrations: [],
    estimatedDescriptors: [],
    // ARNs held back while "use BIN and CAID" is ticked.
    pendingArnCodes: []
  }
}

/* ------------- Slice ------------- */

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    // Replace every server-owned piece of Configure state in one step, from one
    // Configure GET. A replace (not a merge): an empty response is a real answer,
    // and merging would let stale local answers survive it.
    hydrateConfigure(state, { payload }) {
      state.profile.buildingBlocks = payload?.blocks ?? {}
      state.profile.baselineBlocks = payload?.blocks ?? {}
      state.profile.wizard = payload?.wizard ?? null
      state.profile.hydratedBaId = payload?.baId ?? null
      state.profile.baseVersionId = payload?.baseVersionId ?? null
      state.profile.lastUpdated = payload?.lastUpdated ?? state.profile.lastUpdated ?? null
    },
    // Cleared when the account context changes, so nothing renders the previous
    // account's answers while the new request is still in flight.
    resetConfigure(state) {
      state.profile.buildingBlocks = {}
      state.profile.baselineBlocks = {}
      state.profile.wizard = null
      state.profile.hydratedBaId = null
      state.profile.baseVersionId = null
      state.ui.dirtySections = {}
      state.suggestions = initialState.suggestions
    },
    // The wizard block alone, from a save's own response. Only this block: the
    // reply echoes committed steps, so replacing buildingBlocks would drop the
    // edits the merchant is still making on the step they are standing on.
    setWizard(state, { payload }) {
      state.profile.wizard = payload?.wizard ?? state.profile.wizard
    },
    // Local-only field edit — merges into the section's buildingBlocks and marks
    // it dirty. The real PATCH happens in the Footer via externalOps.saveDraft.
    // Dirtiness is a value comparison against the hydrated baseline, not a
    // "field was touched" flag: a merchant who edits Support email and then
    // restores the original address must not be left with a phantom change
    // (which is what showed "Update details" with nothing to update).
    saveSection(state, { payload }) {
      const section = payload?.section
      if (!section) return
      const patch = payload?.patch ?? {}
      const prev = state.profile.buildingBlocks?.[section] ?? {}
      const next = { ...prev, ...patch }
      const baseline = state.profile.baselineBlocks?.[section] ?? {}
      // Changing the country clears State / Region (the hidden code, which the
      // backend then takes from the visitor's IP on save, §11.8, and the name of
      // the old country's region). Going back to the saved country restores both;
      // picking a first country keeps a name typed before it.
      if ("country_code" in patch && patch.country_code !== prev.country_code) {
        if (patch.country_code === baseline.country_code) {
          next.state_region = baseline.state_region ?? ""
          next.state_region_name = baseline.state_region_name ?? ""
        } else if (prev.country_code) {
          next.state_region = ""
          next.state_region_name = ""
        }
      }
      // Clearing "use BIN and CAID" fills in the ARNs the first fill held back, once.
      if (patch.use_bin_caid === false && state.suggestions.pendingArnCodes.length) {
        next.arn_codes = withAdded(next.arn_codes, state.suggestions.pendingArnCodes)
        state.suggestions.pendingArnCodes = []
      }
      state.profile.buildingBlocks[section] = next
      if (isEqual(withoutBlanks(next), withoutBlanks(baseline))) {
        delete state.ui.dirtySections[section]
      } else {
        state.ui.dirtySections[section] = true
      }
    },
    // Clear the dirty flag once a section has been committed to the server.
    clearSectionDirty(state, { payload }) {
      const section = payload?.section
      if (!section) return
      delete state.ui.dirtySections[section]
    },
    // After a profile edit is submitted, drives the amber "changes under review"
    // banner until the backend applies them.
    setPendingChanges(state, { payload }) {
      state.profile.hasPendingChanges = !!payload
    },
    startSuggestions(state, { payload }) {
      state.suggestions.status = SUGGESTIONS_STATUS.LOADING
      state.suggestions.firstFill = payload?.firstFill ?? null
    },
    failSuggestions(state) {
      if (state.suggestions.status !== SUGGESTIONS_STATUS.LOADING) return
      state.suggestions.status = SUGGESTIONS_STATUS.FAILED
    },
    // Leaving Step 3 saves it as it stands, so a first fill still on its way must not
    // change it afterwards: the return shows exactly what was saved (§11.9).
    stopSuggestions(state) {
      if (state.suggestions.status !== SUGGESTIONS_STATUS.LOADING) return
      state.suggestions.status = SUGGESTIONS_STATUS.IDLE
    },
    // Fills Step 3 in from the suggestions. The baseline takes them too: filled-in values
    // are part of Step 3's starting state, so leaving untouched asks nothing (§11.9).
    applySuggestions(state, { payload }) {
      if (state.suggestions.status !== SUGGESTIONS_STATUS.LOADING) return
      const firstFill = payload?.firstFill
      const suggestions = payload?.suggestions || {}
      const descriptorEntries = suggestions.billing_descriptors || []
      const fill = {
        firstFill,
        descriptors: descriptorEntries.map((entry) => entry.value),
        arnCodes: suggestions.arn_codes || [],
        replaceArn: !!payload?.replaceArn
      }
      const current = state.profile.buildingBlocks.payment_identifiers || {}

      state.profile.buildingBlocks.payment_identifiers = withSuggestions(current, fill)
      state.profile.baselineBlocks.payment_identifiers = withSuggestions(
        state.profile.baselineBlocks.payment_identifiers || {},
        fill
      )
      state.suggestions.status = SUGGESTIONS_STATUS.DONE
      state.suggestions.integrations = suggestions.integrations || []
      state.suggestions.estimatedDescriptors = descriptorEntries
        .filter((entry) => entry.is_estimated)
        .map((entry) => entry.value)
      state.suggestions.pendingArnCodes = current.use_bin_caid ? fill.arnCodes : []
    },
    // Footer autosave status pill.
    setAutoSaveStatus(state, { payload }) {
      state.ui.autoSaveStatus = payload || "idle"
    }
  }
})

export const {
  hydrateConfigure,
  resetConfigure,
  setWizard,
  saveSection,
  clearSectionDirty,
  setPendingChanges,
  setAutoSaveStatus,
  startSuggestions,
  failSuggestions,
  stopSuggestions,
  applySuggestions
} = slice.actions

/* ------------- Selectors ------------- */

export const selectBuildingBlocks = (state) => state.receiptConfigure.profile.buildingBlocks
export const selectWizard = (state) => state.receiptConfigure.profile.wizard
export const selectIsHydrated = (state) => !!state.receiptConfigure.profile.hydratedBaId
export const selectBaseVersionId = (state) => state.receiptConfigure.profile.baseVersionId
export const selectLastUpdated = (state) => state.receiptConfigure.profile.lastUpdated
export const selectHasPendingChanges = (state) => state.receiptConfigure.profile.hasPendingChanges
export const selectDirtySections = (state) => state.receiptConfigure.ui.dirtySections
export const selectAutoSaveStatus = (state) => state.receiptConfigure.ui.autoSaveStatus
export const selectSuggestions = (state) => state.receiptConfigure.suggestions

/* ------------- Persistence ------------- */
// Entire slice is session-only: every mount re-fetches via loadProfile() and
// hydrateConfigure() replaces it. Persisting would restore another BA's answers
// before the authoritative GET resolves.

const PERSISTED_REDUCER_VERSION = 1

export const persistedReducer = persistReducer(
  {
    key: "receiptConfigureThunk",
    storage,
    version: PERSISTED_REDUCER_VERSION,
    blacklist: ["profile", "ui", "suggestions"],
    migrate: async (state) => state
  },
  slice.reducer
)

export const reducer = persistedReducer
